import {
  daContextInitialState,
  DaContextProvider
} from "#/components/exam-results/context/descriptive/context";
import type { DaAnswerStatusFilter } from "#/components/exam-results/context/descriptive/types";
import { useSelectedStudentIdContext } from "#/components/exam-results/context/studentsList/context";
import DescriptiveAnswerReportFilter from "#/components/exam-results/descriptiveAnswerTab/DescriptiveAnswerReportFilter";
import verticalizePoemsAndTableLayouts from "#/components/exam-results/descriptivePage/Under500PxLogic";
import EmptyState from "#/components/exam-results/EmptyState";
import { MathJaxContext } from "#/components/mathjax";
import DescriptiveQuestion from "#/components/Question";
import { useGetQuestions } from "#/components/Question/utils";
import type { TExamQuestionsForResultSet } from "#/services/rest/gwt/exam-results/descriptive/exam-question-result-set.types";
import { getModuleGroup } from "#/utils/legacyData/cache/generateHelper";
import { useBreakMD } from "@monta/design-system";
import { useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";

const {
  filters: initialFilters,
  page: initialPage,
  total: initialTotal,
  pageSize
} = daContextInitialState;

const answerStatusFilter = (
  selectedAnswerStatus?: DaAnswerStatusFilter,
  data?: TExamQuestionsForResultSet[]
) => {
  if (!data) return [];

  switch (selectedAnswerStatus) {
    case "all":
      return data;

    case "corrects":
      return data.filter(
        ({ selectedChoiceId, answerId }) => selectedChoiceId === answerId
      );

    case "incorrects":
      return data.filter(
        ({ selectedChoiceId, answerId }) =>
          !!selectedChoiceId && selectedChoiceId !== answerId
      );

    case "no-answers":
      return data.filter(({ selectedChoiceId }) => !selectedChoiceId);

    default:
      return data;
  }
};

const moduleGroupFilter = (
  selectedModuleGroup?: number,
  data?: TExamQuestionsForResultSet[]
) => {
  if (!data) return [];
  if (selectedModuleGroup)
    return data.filter((item) => {
      const moduleGroupItem = getModuleGroup(item.moduleGroupId)?.id;
      return selectedModuleGroup === moduleGroupItem;
    });
  return data;
};

const paginate = (page: number, filteredList: TExamQuestionsForResultSet[]) => {
  const length = filteredList.length;
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, length);
  return filteredList.slice(startIndex, endIndex);
};

const generateFilterModuleGroupItems = (
  details: TExamQuestionsForResultSet[]
) => {
  const items: { label: string; value: string }[] = [];
  details.map((item) => {
    const moduleGroupItem = getModuleGroup(item.moduleGroupId);
    if (
      !items.filter(
        (moduleItem) => moduleItem.value === String(moduleGroupItem?.id)
      ).length
    )
      items.push({
        label: moduleGroupItem?.name ?? "",
        value: String(moduleGroupItem?.id)
      });
  });
  return items;
};

const DescriptiveAnswer = () => {
  const { selectedStudentId } = useSelectedStudentIdContext();
  const data = useGetQuestions(selectedStudentId);
  const breakMD = useBreakMD();
  const [moduleGroupFilters, setModuleGroupFilters] = useState<
    { label: string; value: string }[]
  >([]);
  const [list, setList] = useState<TExamQuestionsForResultSet[]>([]);
  const [page, setPage] = useState(initialPage);
  const [filters, setFilters] = useState(initialFilters);
  const [total, setTotal] = useState(initialTotal);

  useEffect(() => {
    setPage(initialPage);
  }, [selectedStudentId]);

  useEffect(() => {
    if (!data) return;
    const { selectedAnswerStatus, selectedModuleGroup } = filters;
    const filteredAnswerList = answerStatusFilter(selectedAnswerStatus, data);
    const filteredModuleList = moduleGroupFilter(
      selectedModuleGroup,
      filteredAnswerList
    );
    const paginatedList = paginate(page, filteredModuleList);
    setList(paginatedList);
    setTotal(filteredModuleList.length);
  }, [data, filters, page]);

  const value = useMemo(
    () => ({
      filters,
      setFilters,
      page,
      setPage,
      pageSize,
      total,
      setTotal
    }),
    [filters, page, total]
  );

  useEffect(() => {
    if (!list.length) return;
    if (!breakMD) verticalizePoemsAndTableLayouts();
  }, [breakMD, list]);

  useEffect(() => {
    if (data) setModuleGroupFilters(generateFilterModuleGroupItems(data));
  }, []);

  return (
    <DaContextProvider value={value}>
      <DescriptiveAnswerReportFilter data={moduleGroupFilters} />

      {list.length ? (
        <section className={styles["detail-container"]}>
          <MathJaxContext>
            {list.map(({ id }) => (
              <DescriptiveQuestion key={id} id={id} />
            ))}
          </MathJaxContext>
        </section>
      ) : (
        <EmptyState />
      )}
    </DaContextProvider>
  );
};

export default DescriptiveAnswer;
